'use client'

import { ChevronRight, Copy } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'

interface WorkflowInputsContentProps {
  inputs: Record<string, any>
  executionId: string
}

export function WorkflowInputsContent({ inputs, executionId }: WorkflowInputsContentProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Helper function to render type badges
  const TypeBadge = ({ value }: { value: any }) => {
    if (value === null) {
      return (
        <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
          null
        </span>
      )
    }
    if (value === undefined) {
      return (
        <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
          undefined
        </span>
      )
    }
    if (typeof value === 'string') {
      return (
        <span className="rounded border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          string
        </span>
      )
    }
    if (typeof value === 'number') {
      return (
        <span className="rounded border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          number
        </span>
      )
    }
    if (typeof value === 'boolean') {
      return (
        <span className="rounded border border-purple-200 bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
          boolean
        </span>
      )
    }
    if (Array.isArray(value)) {
      return (
        <span className="rounded border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          array
        </span>
      )
    }
    if (typeof value === 'object') {
      return (
        <span className="rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
          object
        </span>
      )
    }
    return (
      <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
        unknown
      </span>
    )
  }

  // Helper component to render structured data
  const DataRenderer = ({ data, depth = 0 }: { data: any; depth?: number }) => {
    const [isDataExpanded, setIsDataExpanded] = useState(false)
    const indentLevel = depth * 16

    if (data === null) {
      return <span className="italic text-gray-600">null</span>
    }

    if (data === undefined) {
      return <span className="italic text-gray-600">undefined</span>
    }

    if (typeof data === 'string') {
      return (
        <span className="break-all font-mono text-sm text-gray-800">{JSON.stringify(data)}</span>
      )
    }

    if (typeof data === 'number') {
      return <span className="font-mono text-sm text-gray-800">{data}</span>
    }

    if (typeof data === 'boolean') {
      return <span className="font-mono text-sm text-gray-800">{String(data)}</span>
    }

    if (Array.isArray(data)) {
      return (
        <div style={{ paddingLeft: `${indentLevel}px` }}>
          <div
            className={`${isDataExpanded ? 'mb-2' : ''} flex items-center gap-2 ${data.length > 0 ? '-mx-2 cursor-pointer rounded px-2 py-1 transition-colors hover:bg-gray-50' : ''}`}
            onClick={() => data.length > 0 && setIsDataExpanded(!isDataExpanded)}
          >
            <span className="text-xs text-gray-600">({data.length} items)</span>
            {data.length > 0 && (
              <ChevronRight
                size={14}
                className={`text-blue-600 transition-all duration-200 ${
                  isDataExpanded ? 'rotate-90' : 'rotate-0'
                }`}
              />
            )}
          </div>
          {isDataExpanded && data.length > 0 && (
            <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
              {data.map((item, index) => (
                <div key={index}>
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">[{index}]</span>
                    <TypeBadge value={item} />
                  </div>
                  <DataRenderer data={item} depth={depth + 1} />
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    if (typeof data === 'object') {
      const entries = Object.entries(data)
      return (
        <div style={{ paddingLeft: `${indentLevel}px` }}>
          <div
            className={`${isDataExpanded ? 'mb-2' : ''} flex items-center gap-2 ${entries.length > 0 ? '-mx-2 cursor-pointer rounded px-2 py-1 transition-colors hover:bg-gray-50' : ''}`}
            onClick={() => entries.length > 0 && setIsDataExpanded(!isDataExpanded)}
          >
            <span className="text-xs text-gray-600">({entries.length} properties)</span>
            {entries.length > 0 && (
              <ChevronRight
                size={14}
                className={`text-blue-600 transition-all duration-200 ${
                  isDataExpanded ? 'rotate-90' : 'rotate-0'
                }`}
              />
            )}
          </div>
          {isDataExpanded && entries.length > 0 && (
            <div className="ml-4 space-y-3 border-l-2 border-gray-200 pl-4">
              {entries.map(([key, value]) => (
                <div key={key}>
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{key}</span>
                    <TypeBadge value={value} />
                  </div>
                  <DataRenderer data={value} depth={depth + 1} />
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    return <span className="font-mono text-sm text-gray-800">{String(data)}</span>
  }

  const inputEntries = Object.entries(inputs)

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50/30">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold text-gray-900">Workflow Inputs</h1>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-600">Execution ID:</span>
            <span className="font-mono text-sm text-gray-700">{executionId}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(executionId)
                toast.success('Copied Execution ID to clipboard')
              }}
              className="cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              title="Copy execution ID"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>

        {/* Inputs */}
        <div className="mb-8">
          <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-base font-semibold text-gray-900">Input Parameters</h3>
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    {inputEntries.length === 0
                      ? 'No inputs provided'
                      : `${inputEntries.length} input parameter${inputEntries.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <ChevronRight
                  size={20}
                  className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                    isExpanded ? 'rotate-90' : 'rotate-0'
                  }`}
                />
              </div>
            </button>
            <div
              className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-6">
                {inputEntries.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 p-4 text-center text-gray-600">
                    No inputs provided for this workflow execution
                  </div>
                ) : (
                  <div className="space-y-4">
                    {inputEntries.map(([key, value]) => (
                      <div key={key} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                        <div className="mb-4 flex items-center gap-2">
                          <h4 className="text-base font-medium text-gray-800">{key}</h4>
                          <TypeBadge value={value} />
                        </div>
                        <DataRenderer data={value} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
